import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';

interface CardDiscount {
  id: number;
  bankName: string;
  cardStartingDigits: string;
  minimumSpend: number;
  maximumDiscountCap: number;
  maximumDailyUses: number;
  maximumMonthlyUses: number;
  discountPercentage: number;
  enabled: boolean;
  timesUsed?: number;
}

type CardDiscountForm = Omit<CardDiscount, 'id'>;

const emptyForm: CardDiscountForm = {
  bankName: '',
  cardStartingDigits: '',
  minimumSpend: 0,
  maximumDiscountCap: 0,
  maximumDailyUses: 1,
  maximumMonthlyUses: 1,
  discountPercentage: 1,
  enabled: true
};

interface FieldsProps {
  value: CardDiscountForm;
  onChange: (next: CardDiscountForm) => void;
  bankNames: string[];
}

const CardDiscountFields: React.FC<FieldsProps> = ({ value, onChange, bankNames }) => {
  const numberChange = (field: keyof CardDiscountForm, raw: string) => {
    onChange({ ...value, [field]: Number(raw) });
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
      <Autocomplete
        freeSolo
        options={bankNames}
        value={value.bankName}
        onChange={(_, newValue) => onChange({ ...value, bankName: newValue || '' })}
        onInputChange={(_, newValue) => onChange({ ...value, bankName: newValue.slice(0, 120) })}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Bank Name"
            required
            helperText="Select an existing bank or type a new name"
            inputProps={{ ...params.inputProps, maxLength: 120 }}
          />
        )}
      />
      <TextField
        label="Card Starting Digits"
        value={value.cardStartingDigits}
        onChange={(event) => onChange({
          ...value,
          cardStartingDigits: event.target.value.replace(/\D/g, '').slice(0, 12)
        })}
        required
        helperText="1 to 12 digits"
        inputProps={{ inputMode: 'numeric', maxLength: 12 }}
      />
      <TextField
        label="Minimum Spend Required (PKR)"
        type="number"
        value={value.minimumSpend}
        onChange={(event) => numberChange('minimumSpend', event.target.value)}
        required
        inputProps={{ min: 0, step: 0.01 }}
      />
      <TextField
        label="Maximum Discount Cap (PKR)"
        type="number"
        value={value.maximumDiscountCap}
        onChange={(event) => numberChange('maximumDiscountCap', event.target.value)}
        required
        inputProps={{ min: 0.01, step: 0.01 }}
      />
      <TextField
        label="Maximum Uses Daily"
        type="number"
        value={value.maximumDailyUses}
        onChange={(event) => numberChange('maximumDailyUses', event.target.value)}
        required
        inputProps={{ min: 1, step: 1 }}
      />
      <TextField
        label="Maximum Uses Monthly"
        type="number"
        value={value.maximumMonthlyUses}
        onChange={(event) => numberChange('maximumMonthlyUses', event.target.value)}
        required
        inputProps={{ min: 1, step: 1 }}
      />
      <TextField
        label="Discount %"
        type="number"
        value={value.discountPercentage}
        onChange={(event) => numberChange('discountPercentage', event.target.value)}
        required
        inputProps={{ min: 0.01, max: 100, step: 0.01 }}
      />
    </Box>
  );
};

const CardDiscounts: React.FC = () => {
  const token = localStorage.getItem('token') || '';
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [cards, setCards] = useState<CardDiscount[]>([]);
  const [form, setForm] = useState<CardDiscountForm>({ ...emptyForm });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editing, setEditing] = useState<CardDiscount | null>(null);
  const [editForm, setEditForm] = useState<CardDiscountForm>({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const bankNames = Array.from(new Set(
    cards.map(card => card.bankName.trim()).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    fetchCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = (error: any, fallback: string) => {
    if (error.response?.status === 401) {
      navigate('/login');
      return;
    }
    const responseMessage = typeof error.response?.data === 'string'
      ? error.response.data
      : error.response?.data?.message;
    setMessage({ type: 'error', text: responseMessage || fallback });
  };

  const fetchCards = async () => {
    setLoading(true);
    try {
      const response = await axios.get<CardDiscount[]>(`${API_BASE_URL}/card-discounts`, {
        headers: { Authorization: token }
      });
      setCards(response.data);
    } catch (error) {
      handleError(error, 'Failed to load card discounts');
    } finally {
      setLoading(false);
    }
  };

  const validate = (value: CardDiscountForm, excludeId?: number) => {
    if (!value.bankName.trim()) return 'Bank name is required';
    if (!/^\d{1,12}$/.test(value.cardStartingDigits)) return 'Starting digits must contain 1 to 12 digits';
    if (cards.some(card =>
      card.cardStartingDigits === value.cardStartingDigits && card.id !== excludeId
    )) {
      return 'A card discount with these starting digits already exists';
    }
    if (value.minimumSpend < 0) return 'Minimum spend cannot be negative';
    if (value.maximumDiscountCap <= 0) return 'Maximum discount cap must be greater than zero';
    if (value.maximumDailyUses < 1 || value.maximumMonthlyUses < 1) return 'Use limits must be at least 1';
    if (value.discountPercentage <= 0 || value.discountPercentage > 100) return 'Discount must be between 0 and 100';
    return null;
  };

  const createCard = async () => {
    const validation = validate(form);
    if (validation) {
      setMessage({ type: 'error', text: validation });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/card-discounts`, form, { headers: { Authorization: token } });
      setForm({ ...emptyForm });
      setShowAddDialog(false);
      setMessage({ type: 'success', text: 'Card discount added' });
      await fetchCards();
    } catch (error) {
      handleError(error, 'Failed to add card discount');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (card: CardDiscount) => {
    setMessage(null);
    setEditing(card);
    setEditForm({
      bankName: card.bankName,
      cardStartingDigits: card.cardStartingDigits,
      minimumSpend: card.minimumSpend,
      maximumDiscountCap: card.maximumDiscountCap,
      maximumDailyUses: card.maximumDailyUses,
      maximumMonthlyUses: card.maximumMonthlyUses,
      discountPercentage: card.discountPercentage,
      enabled: card.enabled
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const validation = validate(editForm, editing.id);
    if (validation) {
      setMessage({ type: 'error', text: validation });
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API_BASE_URL}/card-discounts/${editing.id}`, editForm, {
        headers: { Authorization: token }
      });
      setEditing(null);
      setMessage({ type: 'success', text: 'Card discount updated' });
      await fetchCards();
    } catch (error) {
      handleError(error, 'Failed to update card discount');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEnabled = async (card: CardDiscount) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/card-discounts/${card.id}/enabled?enabled=${!card.enabled}`,
        {},
        { headers: { Authorization: token } }
      );
      setMessage({ type: 'success', text: `Card discount ${card.enabled ? 'disabled' : 'enabled'}` });
      await fetchCards();
    } catch (error) {
      handleError(error, 'Failed to change card discount status');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: 1,
        mb: 2
      }}>
        <Typography variant="h5">Card Discounts</Typography>
        <Button
          variant="contained"
          onClick={() => {
            setForm({ ...emptyForm });
            setMessage(null);
            setShowAddDialog(true);
          }}
          fullWidth={isMobile}
          size={isMobile ? 'medium' : 'small'}
        >
          Add Card
        </Button>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 'calc(100vh - 260px)',
          overflowX: 'auto',
          '& .MuiTableCell-root': {
            padding: { xs: '8px 10px', md: '8px 16px' },
            fontSize: '0.875rem',
            whiteSpace: 'nowrap'
          }
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 1150 }}>
          <TableHead>
            <TableRow>
              <TableCell>Bank</TableCell>
              <TableCell>Starting Digits</TableCell>
              <TableCell align="right">Minimum Spend</TableCell>
              <TableCell align="right">Discount Cap</TableCell>
              <TableCell align="right">Daily Uses</TableCell>
              <TableCell align="right">Monthly Uses</TableCell>
              <TableCell align="right">Discount</TableCell>
              <TableCell align="right">Times Used</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : cards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">No card discounts found</TableCell>
              </TableRow>
            ) : cards.map((card) => (
              <TableRow key={card.id} hover>
                <TableCell>{card.bankName}</TableCell>
                <TableCell>{card.cardStartingDigits}</TableCell>
                <TableCell align="right">PKR {Number(card.minimumSpend).toFixed(2)}</TableCell>
                <TableCell align="right">PKR {Number(card.maximumDiscountCap).toFixed(2)}</TableCell>
                <TableCell align="right">{card.maximumDailyUses}</TableCell>
                <TableCell align="right">{card.maximumMonthlyUses}</TableCell>
                <TableCell align="right">{Number(card.discountPercentage)}%</TableCell>
                <TableCell align="right">{card.timesUsed ?? 0}</TableCell>
                <TableCell>
                  <Box sx={{
                    display: 'inline-block',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: card.enabled ? '#e8f5e8' : '#ffe8e8',
                    color: card.enabled ? '#2e7d32' : '#d32f2f',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    {card.enabled ? 'Enabled' : 'Disabled'}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button variant="outlined" size="small" onClick={() => openEdit(card)}>Edit</Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color={card.enabled ? 'error' : 'success'}
                      onClick={() => toggleEnabled(card)}
                    >
                      {card.enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={showAddDialog}
        onClose={() => !submitting && setShowAddDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Add Card Discount</DialogTitle>
        <DialogContent>
          {message?.type === 'error' && <Alert severity="error" sx={{ mt: 1 }}>{message.text}</Alert>}
          <CardDiscountFields value={form} onChange={setForm} bankNames={bankNames} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowAddDialog(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={createCard} disabled={submitting}>
            {submitting ? <CircularProgress size={22} /> : 'Add Card'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editing)}
        onClose={() => !submitting && setEditing(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Edit Card Discount</DialogTitle>
        <DialogContent>
          {message?.type === 'error' && <Alert severity="error" sx={{ mt: 1 }}>{message.text}</Alert>}
          <CardDiscountFields value={editForm} onChange={setEditForm} bankNames={bankNames} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditing(null)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={submitting}>
            {submitting ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CardDiscounts;
